import React, { useEffect, useState } from 'react';
import { getAllUsers, createUserAdmin, updateUserAdmin, deleteUserAdmin, getClients } from '../../services/api';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaSpinner, FaKey } from 'react-icons/fa';

const emptyUser = {
  nombre_usuario: '',
  password: '',
  email: '',
  id_rol: 2,
  id_cliente: ''
};

const UsersAdminPage = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const loadUsers = async () => {
    const res = await getAllUsers(token);
    if (res.cod === 200) setUsers(res.data);
  };

  const loadClients = async () => {
    const res = await getClients(token);
    if (res.cod === 200) setClients(res.data);
  };

  useEffect(() => { loadUsers(); loadClients(); }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (Number(payload.id_rol) !== 1 && !payload.id_cliente) {
        alert('Debes asociar un cliente para usuarios no administradores');
        return;
      }
      if (!payload.password) delete payload.password;
      if (Number(payload.id_rol) === 1) payload.id_cliente = null;
      if (editing) {
        await updateUserAdmin(token, editing, payload);
      } else {
        await createUserAdmin(token, payload);
      }
      setShowForm(false);
      setEditing(null);
      setForm(emptyUser);
      loadUsers();
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (u) => {
    setForm({ nombre_usuario: u.nombre_usuario, password: '', email: u.email || '', id_rol: u.id_rol, id_cliente: u.id_cliente || '' });
    setEditing(u.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    await deleteUserAdmin(token, id);
    loadUsers();
  };

  const filtered = users.filter(u =>
    !search || u.nombre_usuario?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
          <p className="text-sm text-gray-500 mt-1">{users.length} usuario{users.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { setForm(emptyUser); setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm">
          <FaPlus className="w-3.5 h-3.5" /> Nuevo Usuario
        </button>
      </div>

      <div className="relative mb-4">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          placeholder="Buscar por nombre de usuario o email..." />
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => { setShowForm(false); setEditing(null); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">{editing ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <FaTimes className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col col-span-2">
                <label className="text-xs font-medium text-gray-500 mb-1">Nombre de Usuario</label>
                <input value={form.nombre_usuario} onChange={e => setForm({ ...form, nombre_usuario: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
              </div>
              <div className="flex flex-col col-span-2">
                <label className="text-xs font-medium text-gray-500 mb-1">Email</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
              </div>
              <div className="flex flex-col col-span-2">
                <label className="text-xs font-medium text-gray-500 mb-1">
                  {editing ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña'}
                </label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-500 mb-1">Rol</label>
                <select value={form.id_rol} onChange={e => setForm({ ...form, id_rol: Number(e.target.value) })}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
                  <option value={1}>Administrador</option>
                  <option value={2}>Usuario</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-500 mb-1">Cliente</label>
                <select value={form.id_cliente} onChange={e => setForm({ ...form, id_cliente: e.target.value ? Number(e.target.value) : '' })}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
                  <option value="">Sin cliente</option>
                  {clients.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre} ({c.rut})</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowForm(false); setEditing(null); }}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm">
                {saving && <FaSpinner className="w-3.5 h-3.5 animate-spin" />}
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">ID</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Usuario</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider hidden md:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Rol</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider hidden lg:table-cell">Cliente</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(u => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{u.id}</td>
                  <td className="px-4 py-3 font-medium">{u.nombre_usuario}</td>
                  <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      u.id_rol === 1 ? 'bg-purple-50 text-purple-700' : 'bg-gray-50 text-gray-700'
                    }`}>
                      {u.id_rol === 1 ? 'Admin' : 'Usuario'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">{u.cliente_nombre || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleEdit(u)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar">
                      <FaEdit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(u.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-1" title="Eliminar">
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-gray-400 text-sm">
            <FaSearch className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            {search ? 'No se encontraron usuarios con ese criterio' : 'No hay usuarios registrados'}
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersAdminPage;
